import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HomeHeader } from '../components/ui';
import { HomeFooter } from '../components/ui';

const BulletList = ({ items }) => (
  <List dense sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        sx={{
          display: 'list-item',
          py: 0.25,
          pl: 0,
        }}
      >
        <Typography variant="body1">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <HomeHeader />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button variant="text" sx={{ mb: 2 }} onClick={() => navigate('/')}>
          Back to Home
        </Button>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Privacy Policy
        </Typography>

        <Typography variant="h6" gutterBottom>
          Itclarifies Learning Platform
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Version: 1.0
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Effective Date: June 15, 2026
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Published at:{' '}
          <Link href="https://www.itclarifies.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            https://www.itclarifies.com/privacy-policy
          </Link>
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 3 }}>
          This Privacy Policy explains how Itclarifies AB processes personal data when you
          visit our website, communicate with us, use our services, or access the Itclarifies
          learning platform.
        </Typography>

        <Typography variant="body1" paragraph>
          It also explains the difference between personal data that Itclarifies processes as
          an independent controller and personal data that Itclarifies processes on behalf of a
          customer organization.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Section title="1. Who we are">
          <Typography variant="body1" paragraph>
            Itclarifies AB
            <br />
            Company registration number: 556967-9623
            <br />
            Margretelund 1
            <br />
            184 60 Åkersberga
            <br />
            Sweden
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
          </Typography>

          <Typography variant="body1" paragraph>
            In this Privacy Policy, “Itclarifies”, “we”, “us”, or “our” means Itclarifies AB.
          </Typography>
        </Section>

        <Section title="2. Scope of this Privacy Policy">
          <Typography variant="body1" paragraph>
            This Privacy Policy applies when Itclarifies processes personal data as an
            independent controller, for example when we:
          </Typography>

          <BulletList
            items={[
              'Operate our website.',
              'Manage customer and supplier contacts.',
              'Handle sales, subscriptions, invoicing, and payment administration.',
              'Provide support.',
              'Communicate with customers and users.',
              'Manage security, diagnostics, logs, and platform administration.',
              'Comply with legal obligations.',
              'Protect our legal rights.',
              'Improve and develop our services using limited technical, diagnostic, security, usage, and business-administration data.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Where a customer organization uses the Itclarifies learning platform for its own
            internal learning, training, game-based knowledge activities, examinations,
            learning analytics, or exports, that customer is normally the controller of
            personal data processed inside the customer’s tenant or account.
          </Typography>

          <Typography variant="body1" paragraph>
            In those cases, Itclarifies acts as processor on behalf of the customer under a
            Data Processing Agreement. If you are a user of the Platform through your
            employer or another organization, you should normally contact that organization
            first for questions about how your personal data is used in the Platform.
          </Typography>
        </Section>

        <Section title="3. Customer-controlled platform use">
          <Typography variant="body1" paragraph>
            The Itclarifies platform allows customer organizations to create and run learning
            games, simulations, scenarios, questions, learning sessions, assessments, feedback
            flows, and exports.
          </Typography>

          <Typography variant="body1" paragraph>
            Depending on how the customer uses the Platform, personal data processed in the
            customer’s tenant may include:
          </Typography>

          <BulletList
            items={[
              'Name.',
              'Email address.',
              'Organization affiliation.',
              'User role.',
              'Account identifiers.',
              'Authentication identifiers.',
              'Tenant or organization identifiers.',
              'Game-space or group membership.',
              'Login and access metadata.',
              'IP address.',
              'Device and browser metadata.',
              'Audit logs.',
              'Uploaded learning-content metadata.',
              'Game participation data.',
              'Session identifiers.',
              'Answers submitted by users.',
              'Scores.',
              'Progress data.',
              'Completion status.',
              'Generated feedback.',
              'Examination results.',
              'Exported reports.',
              'Support communications.',
              'Personal data included by the customer in uploaded learning content.',
            ]}
          />

          <Typography variant="body1" paragraph>
            For this customer-controlled processing, the customer decides the purposes and
            means of processing. The customer is responsible for the legal basis, privacy
            notices, internal use of learning results, retention periods, data-subject
            requests, and decisions based on Platform data.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies processes such personal data only to provide, operate, maintain,
            secure, support, and improve the Platform in accordance with the customer’s
            documented instructions, the agreement with the customer, and the applicable Data
            Processing Agreement.
          </Typography>
        </Section>

        <Section title="4. Personal data we process as controller">
          <Typography variant="body1" paragraph>
            When Itclarifies acts as controller, we may process the following categories of
            personal data.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.1 Business contact data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'Name.',
              'Work email address.',
              'Phone number.',
              'Job title.',
              'Organization.',
              'Address.',
              'Communication history.',
              'Meeting information.',
              'Customer, supplier, or partner relationship information.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data to communicate with customers, prospects, suppliers, partners,
            and other business contacts.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.2 Account and administration data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'Name.',
              'Email address.',
              'Organization affiliation.',
              'Role or permission level.',
              'Subscription or order information.',
              'Customer administrator details.',
              'Platform-related contact details.',
              'Support and security contact details.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data to administer customer relationships, manage access, provide
            support, and operate the services.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.3 Billing and payment data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'Customer name.',
              'Company name.',
              'Billing address.',
              'VAT or tax information.',
              'Invoice reference.',
              'Payment status.',
              'Subscription information.',
              'Transaction metadata.',
              'Payment method metadata.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data for invoicing, payment administration, accounting, tax
            compliance, and subscription management.
          </Typography>

          <Typography variant="body1" paragraph>
            We do not intend to store full payment card numbers in the Platform.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.4 Support and communication data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'Name.',
              'Email address.',
              'Organization.',
              'Support request content.',
              'Technical information shared in support requests.',
              'Attachments or files voluntarily sent to us.',
              'Correspondence history.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data to respond to questions, provide support, troubleshoot issues,
            improve service quality, and document support matters.
          </Typography>

          <Typography variant="body1" paragraph>
            You should avoid sending special categories of personal data, confidential
            third-party information, health data, national identification numbers, children’s
            data, or other sensitive information in support messages unless we expressly
            request it and appropriate safeguards have been agreed.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.5 Website, technical, diagnostic, and usage data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'IP address.',
              'Browser type.',
              'Device information.',
              'Operating system.',
              'Referring URLs.',
              'Pages visited.',
              'Timestamps.',
              'Log data.',
              'Error messages.',
              'Security events.',
              'Authentication and access metadata.',
              'Usage and feature-adoption data.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data to operate, secure, troubleshoot, maintain, analyze, and improve
            our website and services.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            4.6 Legal and compliance data
          </Typography>

          <Typography variant="body1" paragraph>
            This may include:
          </Typography>

          <BulletList
            items={[
              'Contract records.',
              'Acceptance records.',
              'Correspondence.',
              'Security records.',
              'Audit information.',
              'Accounting records.',
              'Dispute-related information.',
              'Records required by law.',
            ]}
          />

          <Typography variant="body1" paragraph>
            We use this data to comply with legal obligations, enforce agreements, resolve
            disputes, protect our rights, and document compliance.
          </Typography>
        </Section>

        <Section title="5. Purposes and legal bases">
          <Typography variant="body1" paragraph>
            We process personal data as controller for the following purposes and legal bases.
          </Typography>

          <Table sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Purpose</TableCell>
                <TableCell>Legal basis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Communicating with customers, prospects, partners, and suppliers</TableCell>
                <TableCell>Legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Managing customer relationships and subscriptions</TableCell>
                <TableCell>Contract performance or legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Providing support</TableCell>
                <TableCell>Contract performance or legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Managing billing, invoicing, accounting, and tax records</TableCell>
                <TableCell>Legal obligation or contract performance</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Operating, securing, troubleshooting, and maintaining our services</TableCell>
                <TableCell>Legitimate interests or contract performance</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Managing access to administrative functions and customer contacts</TableCell>
                <TableCell>Contract performance or legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Preventing misuse, fraud, abuse, and security incidents</TableCell>
                <TableCell>Legitimate interests or legal obligation</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Improving and developing our services using limited technical, diagnostic,
                  security, usage, and business-administration data
                </TableCell>
                <TableCell>Legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Handling legal claims, disputes, and compliance matters</TableCell>
                <TableCell>Legal obligation or legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Sending service messages and administrative notices</TableCell>
                <TableCell>Contract performance or legitimate interests</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Sending marketing communications, where applicable</TableCell>
                <TableCell>Consent or legitimate interests, depending on context and applicable law</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Typography variant="body1" paragraph>
            Where we rely on legitimate interests, our interests include operating a secure
            and reliable B2B SaaS platform, communicating with business contacts, supporting
            customers, improving services, preventing misuse, and protecting our legal rights.
          </Typography>
        </Section>

        <Section title="6. AI processing">
          <Typography variant="body1" paragraph>
            The Platform may use AI or language-model functionality to transform
            customer-provided learning materials into games, scenarios, questions, answer
            options, summaries, feedback, examination materials, or similar outputs.
          </Typography>

          <Typography variant="body1" paragraph>
            Where AI services process personal data on behalf of Itclarifies for
            customer-controlled platform use, they are treated as subprocessors under the
            applicable Data Processing Agreement.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies does not use Customer Content or personal data to train, fine-tune, or
            improve general-purpose AI models or third-party AI models unless the customer
            has given prior written consent.
          </Typography>

          <Typography variant="body1" paragraph>
            Customers are responsible for ensuring that personal data included in uploaded
            learning materials is lawful, necessary, proportionate, and appropriate for
            AI-assisted processing.
          </Typography>

          <Typography variant="body1" paragraph>
            Customers should avoid uploading unnecessary personal data into learning
            materials, prompts, free-text answers, or other content submitted for AI-assisted
            processing.
          </Typography>
        </Section>

        <Section title="7. Sensitive personal data">
          <Typography variant="body1" paragraph>
            The Platform is not intended for processing sensitive personal data unless
            expressly agreed in writing.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless expressly agreed, customers and users should not upload or submit:
          </Typography>

          <BulletList
            items={[
              'Special categories of personal data under GDPR Article 9.',
              'Health data.',
              'Biometric data.',
              'Genetic data.',
              'Data revealing racial or ethnic origin.',
              'Data revealing political opinions.',
              'Data revealing religious or philosophical beliefs.',
              'Trade-union membership data.',
              'Sex-life or sexual-orientation data.',
              'Criminal-conviction or offence data.',
              'Swedish personal identity numbers or equivalent national identification numbers.',
              'Children’s personal data.',
              'Patient records.',
              'Government-classified, security-classified, export-controlled, or national-security information.',
              'Highly sensitive HR investigation files.',
            ]}
          />

          <Typography variant="body1" paragraph>
            This does not prohibit customers from uploading ordinary confidential business
            information, internal policies, training materials, strategy documents, process
            descriptions, or other company-confidential materials, provided that the customer
            has the necessary rights to use such materials and the materials do not fall within
            the restricted categories above.
          </Typography>
        </Section>

        <Section title="8. Sources of personal data">
          <Typography variant="body1" paragraph>
            We may receive personal data from:
          </Typography>

          <BulletList
            items={[
              'You directly.',
              'Your employer or organization.',
              'Customer administrators.',
              'Platform users.',
              'Support requests.',
              'Order forms, subscription orders, or invoices.',
              'Authentication providers.',
              'Payment and billing providers.',
              'Website and platform logs.',
              'Business communications.',
              'Public business sources, where relevant.',
            ]}
          />

          <Typography variant="body1" paragraph>
            For personal data processed inside a customer tenant, the customer usually
            provides or controls the personal data.
          </Typography>
        </Section>

        <Section title="9. Sharing personal data">
          <Typography variant="body1" paragraph>
            We may share personal data with:
          </Typography>

          <BulletList
            items={[
              'Cloud hosting and infrastructure providers.',
              'Authentication and identity providers.',
              'AI service providers.',
              'Email, document, and collaboration providers.',
              'Payment and subscription providers.',
              'Accounting and invoicing providers.',
              'Professional advisers.',
              'Authorities, courts, or regulators where required by law.',
              'Subprocessors and service providers used to provide, secure, maintain, and administer the services.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Our current key providers may include:
          </Typography>

          <BulletList
            items={[
              'Microsoft Azure for hosting, database, storage, and infrastructure.',
              'Auth0/Okta for authentication and identity management.',
              'OpenAI for AI-assisted platform functionality.',
              'Google Workspace for business email, documents, calendar, and communications.',
              'Stripe for payment and subscription administration.',
              'Fortnox for invoicing and accounting.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Where these providers process personal data on behalf of Itclarifies for
            customer-controlled platform use, they are listed as subprocessors in the
            applicable Data Processing Agreement.
          </Typography>
        </Section>

        <Section title="10. International transfers">
          <Typography variant="body1" paragraph>
            We are based in Sweden and our primary platform hosting is currently in Microsoft
            Azure West Europe.
          </Typography>

          <Typography variant="body1" paragraph>
            Some providers may process personal data outside the EU/EEA or allow access from
            outside the EU/EEA. For example, AI, authentication, payment, business
            communication, and support-related providers may use global infrastructure or
            subprocessors.
          </Typography>

          <Typography variant="body1" paragraph>
            Where personal data is transferred outside the EU/EEA, we use appropriate
            safeguards where required, such as:
          </Typography>

          <BulletList
            items={[
              'Adequacy decisions.',
              'The European Commission’s Standard Contractual Clauses.',
              'Provider data processing terms.',
              'Other lawful transfer mechanisms under applicable data-protection law.',
            ]}
          />

          <Typography variant="body1" paragraph>
            OpenAI API processing may involve processing outside the EU/EEA. Itclarifies does
            not currently configure or warrant EU-only processing for OpenAI API processing
            unless expressly agreed with a customer.
          </Typography>
        </Section>

        <Section title="11. Retention">
          <Typography variant="body1" paragraph>
            We retain personal data only for as long as necessary for the purposes described
            in this Privacy Policy, unless a longer retention period is required or permitted
            by law.
          </Typography>

          <Typography variant="body1" paragraph>
            Retention periods depend on the type of data and the context.
          </Typography>

          <Typography variant="body1" paragraph>
            Examples:
          </Typography>

          <BulletList
            items={[
              'Business contact data is retained for as long as we have a business relationship or a legitimate reason to maintain the contact.',
              'Customer account and subscription data is retained during the customer relationship and for a reasonable period afterwards.',
              'Billing and accounting data is retained as required by accounting and tax laws.',
              'Support communications are retained for as long as needed to provide support, document issues, improve services, and protect legal rights.',
              'Technical logs are retained for operational, security, troubleshooting, legal, and compliance purposes.',
              'Personal data in customer tenants is retained according to the agreement with the customer, the Data Processing Agreement, customer instructions, platform functionality, backup routines, and applicable law.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Personal data may remain in backups, logs, archives, or recovery systems until
            overwritten or deleted in ordinary retention cycles, provided that the data
            remains protected and is not actively processed except for backup, restoration,
            security, continuity, legal, audit, or compliance purposes.
          </Typography>
        </Section>

        <Section title="12. De-identification, pseudonymisation, and aggregated data">
          <Typography variant="body1" paragraph>
            Itclarifies may process de-identified or aggregated data that no longer identifies
            customers, users, or data subjects.
          </Typography>

          <Typography variant="body1" paragraph>
            We may use such data for analytics, security, product improvement, performance
            monitoring, and business purposes, provided that the data cannot reasonably be
            used to identify individuals.
          </Typography>

          <Typography variant="body1" paragraph>
            Pseudonymised or de-identified data remains personal data unless it has been
            anonymised so that the individual is no longer identifiable by means reasonably
            likely to be used.
          </Typography>

          <Typography variant="body1" paragraph>
            Where a customer removes or deletes a user account through the Platform or
            instructs Itclarifies to do so, Itclarifies may remove or overwrite direct account
            identifiers such as name, username, email address, and external authentication
            identifiers, while retaining internal technical identifiers and historical
            learning records where permitted under the applicable Data Processing Agreement.
          </Typography>
        </Section>

        <Section title="13. Security">
          <Typography variant="body1" paragraph>
            We use technical and organizational measures designed to protect personal data
            against unauthorized or unlawful processing and against accidental or unlawful
            destruction, loss, alteration, unauthorized disclosure, or access.
          </Typography>

          <Typography variant="body1" paragraph>
            Measures may include:
          </Typography>

          <BulletList
            items={[
              'Access controls.',
              'Authentication and authorization mechanisms.',
              'Tenant and role-based separation.',
              'HTTPS/TLS for browser and API traffic.',
              'Provider-supported encryption at rest.',
              'Restricted production access.',
              'Logging and monitoring where reasonably available.',
              'Backup and recovery features provided by infrastructure providers.',
              'Confidentiality obligations.',
              'Use of reputable cloud, identity, AI, payment, and infrastructure providers.',
            ]}
          />

          <Typography variant="body1" paragraph>
            No system can be guaranteed to be completely secure, uninterrupted, or error-free.
          </Typography>

          <Typography variant="body1" paragraph>
            Customers are responsible for security measures within their control, including
            selecting users, managing invitations, roles, permissions, account removal,
            endpoint security, internal policies, user training, and lawful handling of
            exports.
          </Typography>
        </Section>

        <Section title="14. Your rights">
          <Typography variant="body1" paragraph>
            Depending on the circumstances and applicable law, you may have the right to:
          </Typography>

          <BulletList
            items={[
              'Request access to your personal data.',
              'Request correction of inaccurate personal data.',
              'Request deletion of personal data.',
              'Request restriction of processing.',
              'Object to processing.',
              'Request data portability.',
              'Withdraw consent where processing is based on consent.',
              'Lodge a complaint with a supervisory authority.',
            ]}
          />

          <Typography variant="body1" paragraph>
            If your personal data is processed in a customer tenant, such as through your
            employer’s or organization’s use of the Platform, the customer is normally the
            controller. In that case, you should contact the customer first.
          </Typography>

          <Typography variant="body1" paragraph>
            If you contact Itclarifies directly about data controlled by a customer, we may
            refer your request to the relevant customer or ask you to contact the customer
            directly, unless we are legally required to respond differently.
          </Typography>

          <Typography variant="body1" paragraph>
            To exercise rights relating to personal data for which Itclarifies is controller,
            contact us at:
            <br />
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
          </Typography>
        </Section>

        <Section title="15. Supervisory authority">
          <Typography variant="body1" paragraph>
            You have the right to lodge a complaint with a competent data-protection
            authority.
          </Typography>

          <Typography variant="body1" paragraph>
            In Sweden, the supervisory authority is:
            <br />
            Integritetsskyddsmyndigheten
            <br />
            Website:{' '}
            <Link href="https://imy.se" target="_blank" rel="noopener noreferrer">
              imy.se
            </Link>
          </Typography>
        </Section>

        <Section title="16. Cookies and similar technologies">
          <Typography variant="body1" paragraph>
            Our website and Platform may use cookies or similar technologies that are
            necessary to provide, secure, or operate the website and services.
          </Typography>

          <Typography variant="body1" paragraph>
            We may also use cookies or similar technologies for analytics, performance, or
            functionality where permitted by law.
          </Typography>

          <Typography variant="body1" paragraph>
            Where required, we will request consent before using non-essential cookies.
          </Typography>

          <Typography variant="body1" paragraph>
            A separate cookie notice may provide more detailed information about cookies used
            on our website or Platform.
          </Typography>
        </Section>

        <Section title="17. Children">
          <Typography variant="body1" paragraph>
            The Platform is intended for use by organizations, businesses, associations,
            public-sector entities, and other non-consumer customers.
          </Typography>

          <Typography variant="body1" paragraph>
            The Platform is not intended for children’s personal data unless expressly agreed
            in writing with appropriate safeguards.
          </Typography>

          <Typography variant="body1" paragraph>
            Customers must not upload or submit children’s personal data unless expressly
            agreed.
          </Typography>
        </Section>

        <Section title="18. Changes to this Privacy Policy">
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time.
          </Typography>

          <Typography variant="body1" paragraph>
            The latest version will be published on our website. Material changes may also be
            notified by email, through the Platform, or by other appropriate means.
          </Typography>
        </Section>

        <Section title="19. Contact">
          <Typography variant="body1" paragraph>
            For privacy questions, contact:
            <br />
            Itclarifies AB
            <br />
            Margretelund 1
            <br />
            184 60 Åkersberga
            <br />
            Sweden
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
          </Typography>
        </Section>

        <Typography variant="h6" gutterBottom sx={{ mt: 5, fontWeight: 700 }}>
          Change Log / Version History
        </Typography>

        <Table sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Summary of Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>1.0</TableCell>
              <TableCell>2026-06-15</TableCell>
              <TableCell>
                Updated the Privacy Policy for the B2B learning platform, including clearer
                controller and processor roles, customer-controlled platform use, AI processing,
                subprocessors, international transfers, retention, security, data-subject rights,
                and sensitive-data restrictions.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>0.9</TableCell>
              <TableCell>Previous version</TableCell>
              <TableCell>
                Previous pre-1.0 Privacy Policy version.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Container>

      <HomeFooter />
    </>
  );
};

export default PrivacyPolicy;